import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDepartmentWiseReport,
  fetchSessionList,
  fetchProgrammes,
  clearDepartmentReport,
} from "../../redux/actions/attendanceActions";
import {
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiDownload,
  FiX,
  FiUser,
  FiBook,
  FiCheckCircle,
  FiAlertCircle,
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiCalendar,
  FiChevronDown,
  FiBookOpen,
  FiPrinter,
  FiFileText,
  FiBriefcase,
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const DepartmentWiseReports = () => {
  const dispatch = useDispatch();
  const {
    departmentReport,
    departmentPagination,
    loading,
    error,
    sessionList,
    programmes,
  } = useSelector((state) => state.attendance);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedProgramme, setSelectedProgramme] = useState("");

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Search state for filters
  const [sessionSearch, setSessionSearch] = useState("");
  const [programmeSearch, setProgrammeSearch] = useState("");
  const [showSessionDropdown, setShowSessionDropdown] = useState(false);
  const [showProgrammeDropdown, setShowProgrammeDropdown] = useState(false);
  const sessionSelectRef = useRef(null);
  const programmeSelectRef = useRef(null);
  const sessionDropdownRef = useRef(null);
  const programmeDropdownRef = useRef(null);

  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const filteredReportData = useMemo(() => {
    if (!departmentReport || departmentReport.length === 0) return [];

    if (!debouncedSearchTerm.trim()) return departmentReport;

    const searchLower = debouncedSearchTerm.toLowerCase().trim();

    return departmentReport.filter((student) => {
      const studentNameMatch =
        student.student_name?.toLowerCase().includes(searchLower) || false;
      const studentIdMatch =
        student.student_id?.toString().includes(searchLower) || false;
      const collegeRollMatch =
        student.college_roll?.toLowerCase().includes(searchLower) || false;

      return studentNameMatch || studentIdMatch || collegeRollMatch;
    });
  }, [departmentReport, debouncedSearchTerm]);

  const filteredPagination = useMemo(() => {
    if (!filteredReportData || filteredReportData.length === 0) {
      return { total: 0, totalPages: 0 };
    }
    return {
      total: filteredReportData.length,
      totalPages: Math.ceil(filteredReportData.length / limit),
    };
  }, [filteredReportData, limit]);

  // Get current page data from filtered results
  const currentFilteredData = useMemo(() => {
    if (!filteredReportData || filteredReportData.length === 0) return [];
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    return filteredReportData.slice(startIndex, endIndex);
  }, [filteredReportData, currentPage, limit]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const isInitialLoad = useRef(true);

  // Fetch sessions and programmes on mount
  useEffect(() => {
    dispatch(fetchSessionList());
    dispatch(fetchProgrammes());
  }, [dispatch]);

  // Filter sessions based on search
  const filteredSessions =
    sessionList?.filter((session) =>
      session.session.toLowerCase().includes(sessionSearch.toLowerCase()),
    ) || [];

  // Filter programmes based on search
  const filteredProgrammes =
    programmes?.filter((programme) =>
      programme.programme_name
        .toLowerCase()
        .includes(programmeSearch.toLowerCase()),
    ) || [];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSessionDropdown) {
        const isClickInsideSessionButton = sessionSelectRef.current?.contains(
          event.target,
        );
        const isClickInsideSessionDropdown =
          sessionDropdownRef.current?.contains(event.target);

        if (!isClickInsideSessionButton && !isClickInsideSessionDropdown) {
          setShowSessionDropdown(false);
          setSessionSearch("");
        }
      }

      // Close programme dropdown
      if (showProgrammeDropdown) {
        const isClickInsideProgrammeButton =
          programmeSelectRef.current?.contains(event.target);
        const isClickInsideProgrammeDropdown =
          programmeDropdownRef.current?.contains(event.target);

        if (!isClickInsideProgrammeButton && !isClickInsideProgrammeDropdown) {
          setShowProgrammeDropdown(false);
          setProgrammeSearch("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSessionDropdown, showProgrammeDropdown]);

  // Main useEffect to fetch report with filters
  useEffect(() => {
    const timer = setTimeout(
      () => {
        if (selectedSemester && selectedSession && selectedProgramme) {
          dispatch(
            fetchDepartmentWiseReport({
              sem: selectedSemester,
              session_id: selectedSession,
              programme_id: selectedProgramme,
              page: currentPage,
              limit: limit,
            }),
          );
        } else if (!isInitialLoad.current) {
          dispatch(clearDepartmentReport());
        }
      },
      isInitialLoad.current ? 0 : 500,
    );

    isInitialLoad.current = false;
    return () => clearTimeout(timer);
  }, [
    selectedSemester,
    selectedSession,
    selectedProgramme,
    currentPage,
    limit,
    dispatch,
  ]);

  // Clear search term when filters change
  useEffect(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
  }, [selectedSemester, selectedSession, selectedProgramme]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (filteredPagination?.totalPages || 1)) {
      setCurrentPage(newPage);
    }
  };

  // Handle limit change
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  // Handle refresh
  const handleRefresh = () => {
    if (selectedSemester && selectedSession && selectedProgramme) {
      dispatch(
        fetchDepartmentWiseReport({
          sem: selectedSemester,
          session_id: selectedSession,
          programme_id: selectedProgramme,
          page: currentPage,
          limit: limit,
        }),
      );
      setSearchTerm("");
      setDebouncedSearchTerm("");
      toast.success("Report refreshed");
    } else {
      toast.error("Please select all filters first");
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedSemester("");
    setSelectedSession("");
    setSelectedProgramme("");
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
    dispatch(clearDepartmentReport());
    toast.success("Filters cleared");
  };

  // Apply filters
  const handleApplyFilters = () => {
    if (!selectedSemester) {
      toast.error("Please select a semester");
      return;
    }
    if (!selectedSession) {
      toast.error("Please select a session");
      return;
    }
    if (!selectedProgramme) {
      toast.error("Please select a programme");
      return;
    }
    setCurrentPage(1);
    setShowFilters(false);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    toast.success("Filters applied");
  };

  // Get selected session label
  const getSelectedSessionLabel = () => {
    const session = sessionList?.find(
      (s) => s.id === parseInt(selectedSession),
    );
    return session ? session.session : "Select Session";
  };

  // Get selected programme label
  const getSelectedProgrammeLabel = () => {
    const programme = programmes?.find(
      (p) => p.subject_id === selectedProgramme,
    );
    return programme ? programme.programme_name : "Select Programme";
  };

  // Get payment status color
const getPaymentStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "paid":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    case "pending":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    case "unpaid":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
  }
};

  // Get percentage color based on value
  const getPercentageColor = (percentage) => {
    const value = parseFloat(percentage);
    if (value >= 75) return "text-green-600 dark:text-green-400";
    if (value >= 60) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };
// Export to Excel
const handleExportToExcel = () => {
  if (!filteredReportData || filteredReportData.length === 0) {
    toast.error("No data to export");
    return;
  }

  try {
    const exportData = [];
    filteredReportData.forEach((student) => {
      const hasSummary = student.summary;
      
      student.subjects.forEach((subject, index) => {
        exportData.push({
          "Serial No": index === 0 ? student.serial_no : "",
          "Student ID": index === 0 ? student.student_id : "",
          "Student Name": index === 0 ? student.student_name : "",
          "College Roll": index === 0 ? student.college_roll : "",
          "Programme": index === 0 ? student.programme : "",
          "Subject": subject.subject_name,
          "Present Days": subject.present,
          "Absent Days": subject.absent,
          "Attendance %": subject.present_percentage,
          "Total Classes (Overall)": index === 0 && hasSummary ? student.summary.total_classes : "",
          "Present (Overall)": index === 0 && hasSummary ? student.summary.present : "",
          "Absent (Overall)": index === 0 && hasSummary ? student.summary.absent : "",
          "Overall Attendance %": index === 0 && hasSummary ? student.summary.present_percentage : "",
          "Payment Status": index === 0 ? (student.is_paid === 1 ? "Paid" : "Unpaid") : "",
        });
      });
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, "DepartmentWiseReport");
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const filename = `GMGC_Department_Report_${timestamp}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success(`Exported ${exportData.length} records to Excel`);
  } catch (error) {
    console.error("Export error:", error);
    toast.error("Failed to export data");
  }
};

// Export to CSV
const handleExportToCSV = () => {
  if (!filteredReportData || filteredReportData.length === 0) {
    toast.error("No data to export");
    return;
  }

  try {
    const exportData = [];
    filteredReportData.forEach((student) => {
      const hasSummary = student.summary;
      
      student.subjects.forEach((subject, index) => {
        exportData.push({
          "Serial No": index === 0 ? student.serial_no : "",
          "Student ID": index === 0 ? student.student_id : "",
          "Student Name": index === 0 ? student.student_name : "",
          "College Roll": index === 0 ? student.college_roll : "",
          "Programme": index === 0 ? student.programme : "",
          "Subject": subject.subject_name,
          "Present Days": subject.present,
          "Absent Days": subject.absent,
          "Attendance %": subject.present_percentage,
          "Total Classes (Overall)": index === 0 && hasSummary ? student.summary.total_classes : "",
          "Present (Overall)": index === 0 && hasSummary ? student.summary.present : "",
          "Absent (Overall)": index === 0 && hasSummary ? student.summary.absent : "",
          "Overall Attendance %": index === 0 && hasSummary ? student.summary.present_percentage : "",
          "Payment Status": index === 0 ? (student.is_paid === 1 ? "Paid" : "Unpaid") : "",
        });
      });
    });

    const headers = Object.keys(exportData[0]);
    const csvRows = [];
    csvRows.push(headers.join(","));

    for (const row of exportData) {
      const values = headers.map((header) => {
        const value = row[header]?.toString() || "";
        return `"${value.replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(","));
    }

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");

    link.setAttribute("href", url);
    link.setAttribute("download", `GMGC_Department_Report_${timestamp}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${exportData.length} records to CSV`);
  } catch (error) {
    console.error("Export error:", error);
    toast.error("Failed to export data");
  }
};
  // Calculate summary statistics from filtered data
const getSummaryStats = () => {
  if (!filteredReportData || filteredReportData.length === 0) {
    return {
      totalStudents: 0,
      totalSubjects: 0,
      averageAttendance: 0,
      paidStudents: 0,
      pendingStudents: 0,
    };
  }

  let totalSubjectsCount = 0;
  let totalAttendanceSum = 0;
  let attendanceCount = 0;
  let paidCount = 0;
  let pendingCount = 0;

  filteredReportData.forEach((student) => {
    totalSubjectsCount += student.subjects?.length || 0;
    if (student.is_paid === 1) {
      paidCount++;
    } else {
      pendingCount++;
    }

    student.subjects?.forEach((subject) => {
      const percentage = parseFloat(subject.present_percentage);
      if (!isNaN(percentage)) {
        totalAttendanceSum += percentage;
        attendanceCount++;
      }
    });
  });

  return {
    totalStudents: filteredReportData.length,
    totalSubjects: totalSubjectsCount,
    averageAttendance:
      attendanceCount > 0
        ? (totalAttendanceSum / attendanceCount).toFixed(2)
        : 0,
    paidStudents: paidCount,
    pendingStudents: pendingCount,
  };
};

  const stats = getSummaryStats();

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiAlertCircle className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Error Loading Report
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-xl transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Department Wise Reports
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View student attendance reports by department, semester, and session
            at Gokhale Memorial Girls' College
          </p>
        </div>
        <div className="flex items-center gap-3 mt-4 md:mt-0">
          {/* Export Dropdown */}
          <div className="relative group">
            <button
              disabled={!filteredReportData || filteredReportData.length === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg ${
                filteredReportData && filteredReportData.length > 0
                  ? "bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white"
                  : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              }`}
            >
              <FiDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-10">
              <button
                onClick={handleExportToExcel}
                disabled={
                  !filteredReportData || filteredReportData.length === 0
                }
                className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2 text-gray-700 dark:text-gray-300"
              >
                <FiFileText className="w-4 h-4" />
                Export as Excel
              </button>
              <button
                onClick={handleExportToCSV}
                disabled={
                  !filteredReportData || filteredReportData.length === 0
                }
                className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2 text-gray-700 dark:text-gray-300"
              >
                <FiFileText className="w-4 h-4" />
                Export as CSV
              </button>
            </div>
          </div>

          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg"
          >
            <FiRefreshCw
              className={loading ? "animate-spin w-4 h-4" : "w-4 h-4"}
            />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 mb-8">
        <div className="flex items-center gap-3 mb-6">
          <FiFilter className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Report Filters
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Semester Select */}
          <div className="relative group">
            <div className="absolute left-4 top-3 text-gray-400">
              <FiBook className="w-4 h-4" />
            </div>
            <select
              value={selectedSemester}
              onChange={(e) => setSelectedSemester(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400 text-base"
            >
              <option value="">Select Semester</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                <option key={sem} value={sem}>
                  Semester {sem}
                </option>
              ))}
            </select>
          </div>

          {/* Session Select */}
          <div className="relative group" ref={sessionSelectRef}>
            <div className="absolute left-4 top-3 text-gray-400 z-10">
              <FiCalendar className="w-4 h-4" />
            </div>
            <button
              onClick={() => setShowSessionDropdown(!showSessionDropdown)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400 text-left flex items-center justify-between text-base"
            >
              <span className="truncate">{getSelectedSessionLabel()}</span>
              <FiChevronDown
                className={`w-3 h-3 transition-transform ${showSessionDropdown ? "rotate-180" : ""}`}
              />
            </button>

            {showSessionDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 max-h-60 overflow-hidden">
                <div className="p-2 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
                  <input
                    type="text"
                    placeholder="Search session..."
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    value={sessionSearch}
                    onChange={(e) => setSessionSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-auto">
                  {filteredSessions.length > 0 ? (
                    filteredSessions.map((session) => (
                      <button
                        key={session.id}
                        onClick={() => {
                          setSelectedSession(session.id.toString());
                          setShowSessionDropdown(false);
                          setSessionSearch("");
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm ${
                          selectedSession === session.id.toString()
                            ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                            : "text-gray-900 dark:text-white"
                        }`}
                      >
                        {session.session}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      {sessionList?.length === 0 ? "Loading..." : "No sessions"}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Programme Select */}
          <div className="relative group" ref={programmeSelectRef}>
            <div className="absolute left-4 top-3 text-gray-400 z-10">
              <FiBookOpen className="w-4 h-4" />
            </div>
            <button
              onClick={() => setShowProgrammeDropdown(!showProgrammeDropdown)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400 text-left flex items-center justify-between text-base"
            >
              <span className="truncate">{getSelectedProgrammeLabel()}</span>
              <FiChevronDown
                className={`w-3 h-3 transition-transform ${showProgrammeDropdown ? "rotate-180" : ""}`}
              />
            </button>

            {showProgrammeDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 max-h-60 overflow-hidden">
                <div className="p-2 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
                  <input
                    type="text"
                    placeholder="Search programme..."
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    value={programmeSearch}
                    onChange={(e) => setProgrammeSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-auto">
                  {filteredProgrammes.length > 0 ? (
                    filteredProgrammes.map((programme) => (
                      <button
                        key={programme.subject_id}
                        onClick={() => {
                          setSelectedProgramme(programme.subject_id);
                          setShowProgrammeDropdown(false);
                          setProgrammeSearch("");
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm ${
                          selectedProgramme === programme.subject_id
                            ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                            : "text-gray-900 dark:text-white"
                        }`}
                      >
                        {programme.programme_name.length > 30
                          ? programme.programme_name.substring(0, 30) + "..."
                          : programme.programme_name}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      {programmes?.length === 0
                        ? "Loading..."
                        : "No programmes"}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Search Button */}
          <button
            onClick={handleApplyFilters}
            disabled={
              !selectedSemester || !selectedSession || !selectedProgramme
            }
            className={`flex items-center justify-center gap-2 font-semibold py-2 px-4 rounded-lg transition-all duration-300 hover:shadow-lg ${
              selectedSemester && selectedSession && selectedProgramme
                ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            }`}
          >
            <FiSearch className="w-4 h-4" />
            <span>Search</span>
          </button>

          {/* Reset Button */}
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset
          </button>
        </div>

        {/* Search Bar */}
        {selectedSemester &&
          selectedSession &&
          selectedProgramme &&
          departmentReport &&
          departmentReport.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <FiSearch className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  placeholder="Search by Student Name, ID, or College Roll..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400 text-base"
                  autoComplete="off"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setDebouncedSearchTerm("");
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                )}
              </div>
              {debouncedSearchTerm && filteredReportData.length === 0 && (
                <p className="mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <FiAlertCircle className="w-4 h-4" />
                  No students found matching "{debouncedSearchTerm}"
                </p>
              )}
              {debouncedSearchTerm && filteredReportData.length > 0 && (
                <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                  Found {filteredReportData.length} student
                  {filteredReportData.length !== 1 ? "s" : ""} matching "
                  {debouncedSearchTerm}"
                </p>
              )}
            </div>
          )}

        {/* Active Filters Display */}
        {(selectedSemester || selectedSession || selectedProgramme) && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Active filters:
              </span>
              {selectedSemester && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-sm">
                  Semester {selectedSemester}
                  <button
                    onClick={() => setSelectedSemester("")}
                    className="hover:text-purple-900 ml-1"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedSession && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-sm">
                  {getSelectedSessionLabel()}
                  <button
                    onClick={() => setSelectedSession("")}
                    className="hover:text-purple-900 ml-1"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedProgramme && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-sm">
                  {getSelectedProgrammeLabel().substring(0, 40)}...
                  <button
                    onClick={() => setSelectedProgramme("")}
                    className="hover:text-purple-900 ml-1"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {filteredReportData && filteredReportData.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {[
            {
              title: "Total Students",
              value: stats.totalStudents,
              color: "from-purple-600 to-pink-600",
              icon: <FiUser />,
            },
            {
              title: "Total Subjects",
              value: stats.totalSubjects,
              color: "from-blue-600 to-cyan-600",
              icon: <FiBook />,
            },
            {
              title: "Avg Attendance",
              value: `${stats.averageAttendance}%`,
              color: "from-emerald-600 to-green-600",
              icon: <FiTrendingUp />,
            },
            {
              title: "Paid Students",
              value: stats.paidStudents,
              color: "from-green-600 to-emerald-600",
              icon: <FiDollarSign />,
            },
            {
              title: "Pending Payment",
              value: stats.pendingStudents,
              color: "from-amber-600 to-orange-600",
              icon: <FiAlertCircle />,
            },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.1 }}
              className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 dark:border-gray-700 overflow-hidden group"
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5 group-hover:opacity-10 transition-opacity`}
              />
              <div className="relative p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">
                      {stat.title}
                    </p>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                      {stat.value}
                    </h2>
                  </div>
                  <div
                    className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} text-white shadow-lg`}
                  >
                    {stat.icon}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Report Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Department Wise Report
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                {currentFilteredData && currentFilteredData.length > 0
                  ? `Showing ${currentFilteredData.reduce((acc, student) => acc + (student.subjects?.length || 0), 0)} subject records for ${currentFilteredData.length} students`
                  : selectedSemester && selectedSession && selectedProgramme
                    ? debouncedSearchTerm
                      ? `No students found matching "${debouncedSearchTerm}"`
                      : "Loading report data..."
                    : "Please select semester, session, and programme to view report"}
              </p>
            </div>
            {filteredPagination?.totalPages > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Show
                  </span>
                  <select
                    value={limit}
                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="500">500</option>
                    <option value="1000">1000</option>
                  </select>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    entries
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">
              Loading department report...
            </p>
          </div>
        ) : !currentFilteredData || currentFilteredData.length === 0 ? (
          <div className="py-20 text-center">
            <FiBriefcase className="text-6xl text-purple-600 dark:text-purple-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {debouncedSearchTerm
                ? "No matching records found"
                : "Select the correct fields"}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              {debouncedSearchTerm
                ? `No students found matching "${debouncedSearchTerm}" in the current report`
                : selectedSemester && selectedSession && selectedProgramme
                  ? "No records match your selected filters"
                  : "Please select semester, session and programme to generate report"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                  <tr>
                    <th className="text-left py-4 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      S.No
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Student Details
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Programme
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Paper / Subject
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Present
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Absent
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Semester
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Attendance
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Overall Summary
                    </th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Payment Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentFilteredData.map((student, studentIndex) => {
                    const subjects = student.subjects || [];
                    const totalSubjects = subjects.length;
                    const globalSerialNo =
                      (currentPage - 1) * limit + studentIndex + 1;

                    return subjects.map((subject, subjectIndex) => (
                      <motion.tr
                        key={`${student.student_id}-${subjectIndex}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.3,
                          delay: studentIndex * 0.05 + subjectIndex * 0.02,
                        }}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                          subjectIndex === totalSubjects - 1
                            ? "border-b-2 border-gray-200 dark:border-gray-700"
                            : "border-b-0"
                        }`}
                      >
                        {/* Serial No */}
                        <td className="py-4 px-4 align-top">
                          {subjectIndex === 0 ? (
                            <span className="text-gray-900 dark:text-white font-medium">
                              {globalSerialNo}
                            </span>
                          ) : null}
                        </td>

                        {/* Student Details */}
                        <td className="py-4 px-4 align-top">
                          {subjectIndex === 0 ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-medium shadow-md flex-shrink-0">
                                  {student.student_name?.charAt(0) || "S"}
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900 dark:text-white">
                                    {student.student_name}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    ID: {student.student_id}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                    Roll: {student.college_roll}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </td>

                        {/* Programme */}
                        <td className="py-4 px-4 align-top">
                          {subjectIndex === 0 ? (
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {student.programme}
                            </span>
                          ) : null}
                        </td>

                        {/* Paper/Subject Name */}
                        <td className="py-4 px-4">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {subject.subject_name}
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4 align-middle">
                          <p className="inline-flex items-center rounded-xl px-3 py-1 text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-center">
                            {subject.present}
                          </p>
                        </td>

                        <td className="py-4 px-4 align-middle">
                          <p className="inline-flex items-center rounded-xl px-3 py-1 text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-center">
                            {subject.absent}
                          </p>
                        </td>
                        {/* Semester */}
                        <td className="py-4 px-4 align-middle">
                          <span className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-lg text-xs font-medium">
                            Sem - {selectedSemester}
                          </span>
                        </td>

                        {/* Attendance Percentage with visual indicator */}
                        <td className="py-4 px-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-sm font-bold ${getPercentageColor(subject.present_percentage)}`}
                              >
                                {subject.present_percentage}
                              </span>
                              {parseFloat(subject.present_percentage) >= 75 ? (
                                <FiTrendingUp className="text-green-500 w-4 h-4" />
                              ) : parseFloat(subject.present_percentage) >=
                                60 ? (
                                <FiTrendingUp className="text-yellow-500 w-4 h-4" />
                              ) : (
                                <FiTrendingDown className="text-red-500 w-4 h-4" />
                              )}
                            </div>
                            {/* Progress bar */}
                            <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                  parseFloat(subject.present_percentage) >= 75
                                    ? "bg-green-500"
                                    : parseFloat(subject.present_percentage) >=
                                        60
                                      ? "bg-yellow-500"
                                      : "bg-red-500"
                                }`}
                                style={{ width: subject.present_percentage }}
                              />
                            </div>
                          </div>
                        </td>

                        {/* Overall Summary */}
{/* Overall Summary */}
<td
  className="py-4 px-4 align-middle"
  rowSpan={subjectIndex === 0 ? totalSubjects : 1}
>
  {subjectIndex === 0 && student.summary && (
    <div className="space-y-3">
      {/* 3 Flat Tablets */}
      <div className="flex gap-2">
        {/* Total Classes Tablet */}
        <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-lg p-2 text-center shadow-sm border border-gray-200 dark:border-gray-600">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Total</p>
          <p className="text-base font-bold text-gray-800 dark:text-gray-200">
            {student.summary.total_classes}
          </p>
        </div>
        
        {/* Present Tablet */}
        <div className="flex-1 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-lg p-2 text-center shadow-sm border border-emerald-200 dark:border-emerald-800">
          <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Present</p>
          <p className="text-base font-bold text-emerald-700 dark:text-emerald-300">
            {student.summary.present}
          </p>
        </div>
        
        {/* Absent Tablet */}
        <div className="flex-1 bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-900/20 dark:to-red-900/20 rounded-lg p-2 text-center shadow-sm border border-rose-200 dark:border-rose-800">
          <p className="text-xs text-rose-600 dark:text-rose-400 font-medium">Absent</p>
          <p className="text-base font-bold text-rose-700 dark:text-rose-300">
            {student.summary.absent}
          </p>
        </div>
      </div>
      
      {/* Percentage and Bar Graph */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Attendance Rate
          </span>
          <span className={`text-sm font-bold ${getPercentageColor(student.summary.present_percentage)}`}>
            {student.summary.present_percentage}
          </span>
        </div>
        
<div className="flex items-center justify-center p-2">
  <div className="relative w-24 h-24">
    {/* SVG Wrapper */}
    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
      {/* Background Circle (Track) */}
      <circle
        cx="50"
        cy="50"
        r="40"
        stroke="currentColor"
        strokeWidth="8"
        fill="transparent"
        className="text-gray-200 dark:text-gray-700"
      />
      {/* Progress Circle */}
      <circle
        cx="50"
        cy="50"
        r="40"
        stroke="currentColor"
        strokeWidth="8"
        fill="transparent"
        strokeDasharray="251.2"
        strokeDashoffset={251.2 - (251.2 * parseFloat(student.summary.present_percentage)) / 100}
        strokeLinecap="round"
        className={`transition-all duration-700 ease-in-out ${
          parseFloat(student.summary.present_percentage) >= 75
            ? "text-emerald-500"
            : parseFloat(student.summary.present_percentage) >= 60
              ? "text-amber-500"
              : "text-rose-500"
        }`}
      />
    </svg>
    
    {/* Center Percentage Text */}
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="text-lg font-bold text-gray-700 dark:text-gray-200">
        {Math.round(parseFloat(student.summary.present_percentage))}%
      </span>
    </div>
  </div>
</div>
        
        {/* Mini legend */}
        <div className="flex items-center justify-center gap-3 pt-1">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
            <span className="text-xs text-gray-500 dark:text-gray-400">≥75%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
            <span className="text-xs text-gray-500 dark:text-gray-400">60-74%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-rose-500"></div>
            <span className="text-xs text-gray-500 dark:text-gray-400">&lt;60%</span>
          </div>
        </div>
      </div>
    </div>
  )}
</td>

                        {/* Payment Status */}
                        <td
                          className="py-4 px-4 align-top text-center"
                          rowSpan={subjectIndex === 0 ? totalSubjects : 1}
                        >
                          {subjectIndex === 0 && (
                            <span
                              className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getPaymentStatusColor(student.is_paid === 1 ? "Paid" : "Unpaid")}`}
                            >
                              {student.is_paid === 1 ? (
                                <FiCheckCircle className="w-4 h-4 mr-1.5" />
                              ) : (
                                <FiAlertCircle className="w-4 h-4 mr-1.5" />
                              )}
                              {student.is_paid === 1 ? "Paid" : "Unpaid"}
                            </span>
                          )}
                        </td>
                      </motion.tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredPagination?.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Showing {(currentPage - 1) * limit + 1} to{" "}
                    {Math.min(
                      currentPage * limit,
                      filteredPagination?.total || 0,
                    )}{" "}
                    of {filteredPagination?.total || 0} records
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${
                        currentPage === 1
                          ? "bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                          : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      <FiChevronLeft /> Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from(
                        {
                          length: Math.min(
                            5,
                            filteredPagination?.totalPages || 1,
                          ),
                        },
                        (_, i) => {
                          let pageNum;
                          if ((filteredPagination?.totalPages || 1) <= 5)
                            pageNum = i + 1;
                          else if (currentPage <= 3) pageNum = i + 1;
                          else if (
                            currentPage >=
                            (filteredPagination?.totalPages || 1) - 2
                          )
                            pageNum =
                              (filteredPagination?.totalPages || 1) - 4 + i;
                          else pageNum = currentPage - 2 + i;
                          if (
                            pageNum < 1 ||
                            pageNum > (filteredPagination?.totalPages || 1)
                          )
                            return null;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`w-10 h-10 rounded-lg transition ${
                                currentPage === pageNum
                                  ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
                                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        },
                      )}
                    </div>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={
                        currentPage === (filteredPagination?.totalPages || 1)
                      }
                      className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${
                        currentPage === (filteredPagination?.totalPages || 1)
                          ? "bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                          : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      Next <FiChevronRight />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DepartmentWiseReports;
